import React, { useEffect, useState } from 'react';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import SectionHeading from '../ui/SectionHeading';
import { colors, font } from '../../lib/tokens';
import { NEWS, newsPath } from '../../data/news';

// Each item is shareable at /news/<slug>. Netlify serves a prerendered page
// with per-item Open Graph tags at that path (scripts/generate-news-pages.js);
// once the SPA boots, this hook scrolls the matching record into view.
function useNewsDeepLink() {
  useEffect(() => {
    const match = window.location.pathname.match(/^\/news\/([^/]+)\/?$/);
    if (!match) return;
    const item = NEWS.find((n) => n.slug === decodeURIComponent(match[1]));
    if (!item) return;
    document.title = `${item.title} — KatlaCode`;
    const el = document.getElementById(`news-${item.slug}`);
    if (!el) return;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('kc-news-highlight');
    }, 150);
    return () => window.clearTimeout(timer);
  }, []);
}

function CopyPermalink({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const url = `${window.location.origin}${newsPath(slug)}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(() => window.prompt('Copy this link:', url));
    } else {
      window.prompt('Copy this link:', url);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <a
      href={newsPath(slug)}
      className="kc-news-copy"
      onClick={(e) => {
        e.preventDefault();
        copy();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        ...(copied ? { color: colors.blue } : null),
        fontFamily: font.mono,
        fontSize: 11,
        letterSpacing: 1,
        textTransform: 'uppercase',
      }}
    >
      {copied ? (
        // The link icon pops into a checkmark to confirm the copy.
        <svg className="kc-pop" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 12.5l5 5L20 6.5" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      )}
      {copied ? 'Link copied' : 'Copy link'}
    </a>
  );
}

export default function NewsSection() {
  useNewsDeepLink();

  return (
    <section
      id="news"
      className="kc-section"
      style={{
        background: colors.surface,
        borderTop: `1px solid ${colors.hair}`,
      }}
    >
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div data-reveal>
            <Eyebrow>News</Eyebrow>
            <SectionHeading style={{ marginBottom: 24 }}>
              Recent
              {' '}<br />
              milestones.
            </SectionHeading>
            <p style={{ color: colors.muted, fontSize: 16, lineHeight: 1.65, maxWidth: 360, margin: 0 }}>
              Funding and progress as we build clinical-coding infrastructure inside hospitals.
            </p>
          </div>
          <div data-reveal style={{ '--d': '80ms' } as React.CSSProperties}>
            {NEWS.map((n, i) => (
              <div
                key={n.slug}
                id={`news-${n.slug}`}
                className="kc-label-row"
                style={{
                  padding: '28px 0',
                  borderTop: `1px solid ${colors.hair}`,
                  borderBottom: i === NEWS.length - 1 ? `1px solid ${colors.hair}` : 'none',
                }}
              >
                <div
                  className="kc-label-row-label"
                  style={{
                    fontFamily: font.mono,
                    fontSize: 11,
                    color: colors.muted,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  {n.date}
                </div>
                <div>
                  <div
                    className="text-balance"
                    style={{
                      fontSize: 20,
                      fontWeight: 500,
                      color: colors.ink,
                      letterSpacing: -0.3,
                      lineHeight: 1.3,
                    }}
                  >
                    {n.title}
                  </div>
                  <p style={{ color: colors.muted, fontSize: 15, lineHeight: 1.65, margin: '10px 0 0' }}>
                    {n.body}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
                    {n.href && (
                      <a
                        href={n.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="kc-news-link"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 7,
                          fontFamily: font.sans,
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {n.linkLabel}
                        <span className="kc-news-arrow">→</span>
                      </a>
                    )}
                    <CopyPermalink slug={n.slug} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
